import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const getInitials = (name = "") =>
  name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

const getTimeOfDay = () => {
  const h = new Date().getHours();

  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";

  return "Good evening";
};

export default function WelcomePage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [mounted, setMounted] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleLogout = async () => {
    try {
      setLoggingOut(true);
      await logout();
      navigate("/");
    } catch (err) {
      console.error(err);
    } finally {
      setLoggingOut(false);
    }
  };

  const joinedDate = user?.createdAt
    ? new Date(user.createdAt).toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
      })
    : "2026";

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:wght@300;400;500;600;700&display=swap');

        *{
          margin:0;
          padding:0;
          box-sizing:border-box;
        }

        :root{
          --bg:#07070a;
          --card:#111114;
          --card-2:#17171c;
          --line:rgba(255,255,255,0.08);
          --text:#f5f7ff;
          --muted:#9ca3af;
          --accent:#8b5cf6;
          --accent-2:#ec4899;
          --green:#34d399;
        }

        html,
        body{
          background:var(--bg);
          color:var(--text);
          font-family:'DM Sans',sans-serif;
        }

        .page{
          min-height:100vh;
          overflow:hidden;
          position:relative;
          background:
            radial-gradient(circle at top left, rgba(139,92,246,.18), transparent 30%),
            radial-gradient(circle at bottom right, rgba(236,72,153,.12), transparent 35%),
            var(--bg);
        }

        .page::before{
          content:'';
          position:absolute;
          inset:0;
          background-image:
            linear-gradient(rgba(255,255,255,.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,.03) 1px, transparent 1px);
          background-size:40px 40px;
          pointer-events:none;
        }

        /* NAVBAR */

        .navbar{
          width:100%;
          display:flex;
          align-items:center;
          justify-content:space-between;
          padding:24px 40px;
          position:sticky;
          top:0;
          z-index:50;
          backdrop-filter:blur(12px);
          background:rgba(7,7,10,.55);
          border-bottom:1px solid var(--line);
        }

        .brand{
          display:flex;
          align-items:center;
          gap:12px;
        }

        .brand-logo{
          width:42px;
          height:42px;
          border-radius:14px;
          background:linear-gradient(135deg,var(--accent),var(--accent-2));
          display:grid;
          place-items:center;
          font-size:18px;
          font-weight:700;
        }

       .brand{
  display:flex;
  align-items:center;
  gap:12px;
}

.brand-text{
  display:flex;
  flex-direction:column;
  line-height:1.1;
}

.brand-name{
  font-size:17px;
  font-weight:700;
  color:var(--text);
}

.tagline{
  margin-top:32px;
  margin-left:-90px;
  font-size:11px;
  font-weight:500;
  color:var(--muted);
  letter-spacing:.3px;
}
        .nav-actions{
          display:flex;
          gap:12px;
        }

        /* BUTTONS */

        .primary-btn{
          border:none;
          outline:none;
          cursor:pointer;
          padding:14px 22px;
          border-radius:16px;
          background:linear-gradient(135deg,var(--accent),var(--accent-2));
          color:white;
          font-weight:600;
          display:flex;
          align-items:center;
          justify-content:center;
          gap:10px;
          transition:.25s;
          box-shadow:0 10px 35px rgba(139,92,246,.28);
        }

        .primary-btn:hover{
          transform:translateY(-3px);
        }

        .secondary-btn{
          border:1px solid var(--line);
          background:rgba(255,255,255,.03);
          color:var(--text);
          padding:14px 20px;
          border-radius:16px;
          cursor:pointer;
          font-weight:500;
          transition:.25s;
        }

        .secondary-btn:hover{
          background:rgba(255,255,255,.06);
        }

        /* HERO */

        .hero{
          max-width:1300px;
          margin:auto;
          padding:80px 40px 40px;
          display:grid;
          grid-template-columns:1.2fr .8fr;
          gap:40px;
          align-items:center;
        }

        .hero-left{
          opacity:${mounted ? 1 : 0};
          transform:${mounted ? "translateY(0px)" : "translateY(40px)"};
          transition:.8s ease;
        }

        .badge{
          display:inline-flex;
          align-items:center;
          gap:8px;
          padding:8px 16px;
          border-radius:999px;
          background:rgba(52,211,153,.08);
          border:1px solid rgba(52,211,153,.15);
          color:var(--green);
          margin-bottom:24px;
          font-size:13px;
        }

        .hero-title{
          font-family:'Instrument Serif',serif;
          font-size:clamp(48px,8vw,82px);
          line-height:1;
          letter-spacing:-2px;
          margin-bottom:22px;
        }

        .hero-title span{
          background:linear-gradient(135deg,var(--accent),var(--accent-2));
          -webkit-background-clip:text;
          -webkit-text-fill-color:transparent;
        }

        .hero-sub{
          max-width:620px;
          color:var(--muted);
          font-size:17px;
          line-height:1.8;
          margin-bottom:34px;
        }

        .hero-buttons{
          display:flex;
          gap:16px;
          flex-wrap:wrap;
        }

        /* PROFILE CARD */

        .hero-card{
          background:rgba(255,255,255,.04);
          border:1px solid var(--line);
          border-radius:32px;
          padding:28px;
          backdrop-filter:blur(16px);

          opacity:${mounted ? 1 : 0};
          transform:${mounted ? "translateY(0px)" : "translateY(40px)"};
          transition:1s ease;
          animation:float 5s ease-in-out infinite;
        }

        .profile-top{
          display:flex;
          align-items:center;
          gap:16px;
          margin-bottom:24px;
        }

        .avatar{
          width:72px;
          height:72px;
          border-radius:22px;
          background:linear-gradient(135deg,var(--accent),var(--accent-2));
          display:grid;
          place-items:center;
          overflow:hidden;
          font-size:24px;
          font-weight:700;
          color:white;
        }

        .avatar img{
          width:100%;
          height:100%;
          object-fit:cover;
        }

        .profile-name{
          font-size:20px;
          font-weight:700;
          margin-bottom:6px;
        }

        .profile-email{
          color:var(--muted);
          font-size:14px;
        }

        .stats{
          display:grid;
          grid-template-columns:1fr 1fr;
          gap:16px;
          margin-top:22px;
        }

        .stat{
          background:rgba(255,255,255,.03);
          border:1px solid var(--line);
          border-radius:18px;
          padding:18px;
        }

        .stat h3{
          font-size:22px;
          margin-bottom:6px;
        }

        .stat p{
          color:var(--muted);
          font-size:13px;
        }

        /* SECTION */

        .section{
          max-width:1300px;
          margin:auto;
          padding:20px 40px 120px;
        }

        .section-title{
          font-size:28px;
          font-weight:700;
          margin-bottom:24px;
        }

        .grid{
          display:grid;
          grid-template-columns:repeat(auto-fit,minmax(240px,1fr));
          gap:22px;
        }

        .action-card{
          background:var(--card);
          border:1px solid var(--line);
          border-radius:26px;
          padding:28px;
          transition:.3s;
          cursor:pointer;
          position:relative;
          overflow:hidden;
        }

        .action-card::before{
          content:'';
          position:absolute;
          inset:0;
          background:linear-gradient(
            135deg,
            rgba(139,92,246,.08),
            rgba(236,72,153,.05)
          );
          opacity:0;
          transition:.3s;
        }

        .action-card:hover{
          transform:translateY(-6px);
          border-color:rgba(139,92,246,.3);
        }

        .action-card:hover::before{
          opacity:1;
        }

        .action-icon{
          width:54px;
          height:54px;
          border-radius:18px;
          background:rgba(139,92,246,.14);
          display:grid;
          place-items:center;
          margin-bottom:18px;
          font-size:24px;
        }

        .action-title{
          font-size:18px;
          font-weight:700;
          margin-bottom:10px;
          position:relative;
          z-index:2;
        }

        .action-sub{
          color:var(--muted);
          line-height:1.6;
          font-size:14px;
          position:relative;
          z-index:2;
        }

        /* SESSION BAR */

        .session-bar{
          position:fixed;
          left:50%;
          transform:translateX(-50%);
          bottom:24px;
          width:min(92%,800px);

          background:rgba(17,17,20,.85);
          border:1px solid var(--line);

          backdrop-filter:blur(18px);

          border-radius:24px;

          padding:18px 24px;

          display:flex;
          justify-content:space-between;
          align-items:center;

          z-index:100;
        }

        .session-left{
          display:flex;
          align-items:center;
          gap:14px;
        }

        .dot{
          width:12px;
          height:12px;
          border-radius:50%;
          background:var(--green);
          box-shadow:0 0 14px rgba(52,211,153,.6);
        }

        .session-title{
          font-size:15px;
          font-weight:600;
        }

        .session-sub{
          color:var(--muted);
          font-size:13px;
          margin-top:3px;
        }

        .spinner{
          width:16px;
          height:16px;
          border:2px solid rgba(255,255,255,.2);
          border-top-color:white;
          border-radius:50%;
          animation:spin .7s linear infinite;
        }

        @keyframes spin{
          to{
            transform:rotate(360deg);
          }
        }

        @keyframes float{
          0%,100%{
            transform:translateY(0px);
          }
          50%{
            transform:translateY(-8px);
          }
        }

        @media(max-width:950px){

          .hero{
            grid-template-columns:1fr;
          }

          .hero-title{
            font-size:58px;
          }
        }

        @media(max-width:640px){

          .navbar{
            padding:18px 20px;
          }

          .hero{
            padding:50px 20px 20px;
          }

          .section{
            padding:20px 20px 120px;
          }

          .hero-title{
            font-size:46px;
          }

          .hero-buttons{
            flex-direction:column;
          }

          .primary-btn,
          .secondary-btn{
            width:100%;
          }

          .stats{
            grid-template-columns:1fr;
          }

          .session-bar{
            flex-direction:column;
            gap:16px;
            align-items:flex-start;
          }
        }
       .profile-details{
  margin-top:28px;

  display:grid;
  grid-template-columns:1fr 1fr;

  gap:14px;
}

.detail-item{
  display:flex;
  align-items:center;
  justify-content:space-between;

  padding:16px 18px;

  background:rgba(255,255,255,.03);

  border:1px solid var(--line);

  border-radius:18px;

  transition:.25s ease;
}

.detail-item:hover{
  border-color:rgba(139,92,246,.25);
  background:rgba(255,255,255,.045);
  transform:translateY(-2px);
}

.detail-key{
  color:var(--muted);

  font-size:13px;

  font-weight:500;
}

.detail-value{
  color:var(--text);

  font-size:13px;

  font-weight:600;
}

.verified-badge{
  display:inline-flex;
  align-items:center;

  padding:6px 11px;

  border-radius:999px;

  background:rgba(52,211,153,.12);

  border:1px solid rgba(52,211,153,.18);

  color:#34d399;

  font-size:11px;

  font-weight:600;
}

.auth-badge{
  display:inline-flex;
  align-items:center;
  justify-content:center;

  padding:6px 11px;

  border-radius:999px;

  background:rgba(139,92,246,.14);

  border:1px solid rgba(139,92,246,.22);

  color:#c4b5fd;

  font-size:11px;

  font-weight:600;

  letter-spacing:.2px;
}

@media(max-width:640px){

  .profile-details{
    grid-template-columns:1fr;
  }

}
      `}</style>

      <div className="page">
        {/* NAVBAR */}
        <nav className="navbar">
          <div className="brand">
            {/* <div className="brand-logo">✦</div> */}
            <div className="brand-logo"><img src="./logo.jpg" width={42} height={42} alt="logo" /></div>
            <div className="brand-name">Invoice AI</div>
            <div className="tagline">by vrsafetech</div>
          </div>

          <div className="nav-actions">
            <button
              className="secondary-btn"
              onClick={handleLogout}
              disabled={loggingOut}
            >
              {loggingOut ? (
                <>
                  <span className="spinner" />
                </>
              ) : (
                "Logout"
              )}
            </button>
          </div>
        </nav>

        {/* HERO */}
        <section className="hero">
          <div className="hero-left">
            <div className="badge">
              ● Secure Authentication Enabled
            </div>

            <h1 className="hero-title">
              {getTimeOfDay()}, <br />
              <span>{user?.name || "Creator"}</span>
            </h1>

            <p className="hero-sub">
             Create invoices faster, manage clients efficiently, track payments securely,
  and run your billing workflow seamlessly from one modern dashboard.
            </p>

            <div className="hero-buttons">
              {/* METHOD 1 */}
              <button
                className="primary-btn"
                onClick={() => navigate("/dashboard")}
              >
                Open Dashboard →
              </button>

              {/* METHOD 2 */}
              <button
                className="secondary-btn"
                onClick={() => navigate("/invoice/ai")}
              >
                 Generate With AI
              </button>
            </div>
          </div>

          {/* PROFILE CARD */}
          <div className="hero-card">
            <div className="profile-top">
              <div className="avatar">
                {user?.picture ? (
                  <img
                    src={user.picture}
                    alt={user.name}
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  getInitials(user?.name)
                )}
              </div>

              <div>
                <div className="profile-name">
                  {user?.name || "User"}
                </div>

                <div className="profile-email">
                  {user?.email}
                </div>
              </div>
            </div>

            <div className="stats">
             <div className="profile-details">

  <div className="detail-item">
    <span className="detail-key">Status</span>

    <span className="verified-badge">
      ✓ Verified
    </span>
  </div>

  <div className="detail-item">
    <span className="detail-key">
      Auth method
    </span>

    <span className="auth-badge">
      {user?.googleId ? "Google OAuth" : "Email OTP"}
    </span>
  </div>

  <div className="detail-item">
    <span className="detail-key">
      Security
    </span>

    <span className="auth-badge">
      JWT Secure
    </span>
  </div>

  {/* <div className="detail-item">
    <span className="detail-key">
      Session
    </span>

    <span className="auth-badge">
      HTTP-only Cookies
    </span>
  </div> */}

  <div className="detail-item">
    <span className="detail-key">
      Member since
    </span>

    <span className="detail-value">
      {joinedDate}
    </span>
  </div>
<div className="detail-item">
  <span className="detail-key">
    Voice ID
  </span>

  <span
    className="auth-badge"
    style={{
      letterSpacing: "1px",
      fontSize: "12px",
    }}
  >
    🎤 {user?.voiceId || "Not Assigned"}
  </span>
</div>
</div>
            </div>
          </div>
        </section>

        {/* QUICK ACTIONS */}
        <section className="section">
          <h2 className="section-title">
            Quick Actions
          </h2>

          <div className="grid">
            {/* METHOD 3 */}
            <div
              className="action-card"
              onClick={() => navigate("/dashboard")}
            >
              <div className="action-icon">🚀</div>

              <div className="action-title">
                Launch Dashboard
              </div>

              <div className="action-sub">
                Open your main workspace and continue instantly.
              </div>
            </div>

            {/* METHOD 4 */}
            <div
              className="action-card"
              onClick={() => navigate("/invoices")}
            >
           
  <div className="action-icon">📄</div>

  <div className="action-title">
    View Invoice Details
  </div>

  <div className="action-sub">
    Open complete invoice information, GST breakdown, and payment status.
  </div>

            </div>

            {/* METHOD 5 */}
            <div
  className="action-card"
  onClick={() => navigate("/invoice/ai")}
>
  <div className="action-icon">✨</div>

  <div className="action-title">
    AI Invoice Generator
  </div>

  <div className="action-sub">
    Generate professional GST invoices instantly using AI prompts.
  </div>
</div>

            {/* METHOD 6 */}
            <div
  className="action-card"
  onClick={() => navigate("/billing")}
>
  <div className="action-icon">💳</div>

  <div className="action-title">
    Billing & Plans
  </div>

  <div className="action-sub">
    Manage subscriptions, invoice limits, and payment history.
  </div>
</div>
          </div>
        </section>

        {/* FLOATING SESSION BAR */}
        {/* <div className="session-bar">
          <div className="session-left">
            <div className="dot"></div>

            <div>
              <div className="session-title">
                Secure session active
              </div>

              <div className="session-sub">
                Tokens refresh automatically in the background
              </div>
            </div>
          </div>

          {/* METHOD 7 *
          <button
            className="primary-btn"
            onClick={() => navigate("/dashboard")}
          >
            Continue →
          </button>
        </div> */}
      </div>
    </>
  );
}